import React, { useEffect, useState } from 'react';
import {
  Container,
  Row,
  Col,
  Card,
  Button,
  Modal,
  Badge,
  Form,
  Spinner,
  Tab,
  Nav,
  Pagination,
  InputGroup,
  DropdownButton,
  Dropdown
} from 'react-bootstrap';
import { 
  FaSearch, 
  FaImage, 
  FaVideo, 
  FaPlayCircle,
  FaTimes,
  FaLeaf,
  FaCalendarAlt,
  FaBox,
  FaPlane,
  FaGraduationCap,
  FaEllipsisH,
  FaChevronLeft,
  FaChevronRight,
  FaThLarge,
  FaThList,
  FaSort,
  FaDownload,
  FaShareAlt,
  FaHeart,
  FaRegHeart,
  FaEye,
  FaClock
} from 'react-icons/fa';
import { BASE_URL, UPLOAD_BASE_URL } from '../../services/api';

// Inline CSS Styles
const styles = `
/* Modern Gallery Styles */
.gallery-container {
  --primary-color: #4361ee;
  --secondary-color: #3a0ca3;
  --accent-color: #f72585;
  --success-color: #4cc9f0;
  --light-bg: #f8f9fa;
  --dark-bg: #212529;
  --card-shadow: 0 10px 30px rgba(0, 0, 0, 0.08);
  --hover-shadow: 0 20px 40px rgba(0, 0, 0, 0.15);
  font-family: 'Inter', 'Segoe UI', system-ui, sans-serif;
}

/* Hero Section - Glass Morphism */
.gallery-hero {
  position: relative;
  background: linear-gradient(135deg, #3a0ca3 0%, #4361ee 50%, #4cc9f0 100%);
  color: white;
  padding: 6rem 0 4rem;
  overflow: hidden;
  clip-path: polygon(0 0, 100% 0, 100% 85%, 0 100%);
}

.hero-overlay {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: linear-gradient(45deg, rgba(0,0,0,0.2), rgba(0,0,0,0.4));
}

.gallery-hero h1 {
  font-weight: 800;
  text-shadow: 2px 2px 4px rgba(0,0,0,0.3);
  margin-bottom: 1.5rem;
  background: linear-gradient(90deg, #fff, #4cc9f0);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
}

.hero-stats {
  display: flex;
  gap: 2rem;
  margin-top: 2.5rem;
}

.stat-box {
  text-align: center;
  padding: 1.2rem 1.5rem;
  background: rgba(255, 255, 255, 0.15);
  backdrop-filter: blur(10px);
  border-radius: 16px;
  border: 1px solid rgba(255, 255, 255, 0.2);
  transition: transform 0.3s ease;
}

.stat-box:hover {
  transform: translateY(-5px);
  background: rgba(255, 255, 255, 0.25);
}

.stat-box h3 {
  font-size: 2.5rem;
  font-weight: 700;
  margin-bottom: 0.5rem;
  background: white;
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
}

.hero-gallery-preview {
  display: grid;
  grid-template-columns: 1fr 1fr;
  grid-template-rows: 1fr 1fr;
  gap: 15px;
  height: 320px;
  border-radius: 20px;
  overflow: hidden;
  position: relative;
  transform: perspective(1000px) rotateY(-5deg);
  box-shadow: -30px 30px 50px rgba(0,0,0,0.3);
}

.preview-item {
  position: relative;
  overflow: hidden;
  cursor: pointer;
  border-radius: 10px;
  transition: all 0.4s cubic-bezier(0.4, 0, 0.2, 1);
}

.preview-item::before {
  content: '';
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: linear-gradient(to bottom, transparent 50%, rgba(0,0,0,0.7));
  z-index: 1;
  opacity: 0;
  transition: opacity 0.3s ease;
}

.preview-item:hover {
  transform: scale(1.05);
  z-index: 2;
}

.preview-item:hover::before {
  opacity: 1;
}

.preview-item img {
  width: 100%;
  height: 100%;
  object-fit: cover;
  transition: transform 0.6s ease;
}

.preview-item:hover img {
  transform: scale(1.1);
}

.video-preview {
  position: relative;
  width: 100%;
  height: 100%;
}

.play-overlay {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.3);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 2;
  opacity: 0;
  transition: opacity 0.3s ease;
}

.video-preview:hover .play-overlay {
  opacity: 1;
}

.play-overlay svg {
  font-size: 3rem;
  color: white;
  filter: drop-shadow(2px 2px 4px rgba(0,0,0,0.5));
}

/* Floating Controls */
.gallery-controls {
  background: white;
  padding: 1.5rem 0;
  margin-top: -2rem;
  position: relative;
  z-index: 10;
  border-radius: 20px;
  box-shadow: var(--card-shadow);
  border: 1px solid rgba(0,0,0,0.05);
}

.gallery-controls .input-group {
  max-width: 400px;
  border-radius: 12px;
  overflow: hidden;
  box-shadow: 0 4px 15px rgba(0,0,0,0.1);
}

.gallery-controls .form-control {
  border: none;
  padding: 0.75rem 1.25rem;
  font-size: 0.95rem;
}

.gallery-controls .btn {
  padding: 0.75rem 1.5rem;
  font-weight: 600;
  background: var(--primary-color);
  border: none;
}

.category-btn {
  padding: 0.5rem 1.2rem;
  border-radius: 25px;
  font-weight: 500;
  transition: all 0.3s ease;
  border: 2px solid transparent;
}

.category-btn:hover {
  transform: translateY(-2px);
  box-shadow: 0 5px 15px rgba(0,0,0,0.1);
}

.btn-group .btn {
  padding: 0.6rem 1.2rem;
  font-weight: 600;
}

/* Modern Cards */
.gallery-card {
  border: none;
  border-radius: 20px;
  overflow: hidden;
  transition: all 0.4s cubic-bezier(0.4, 0, 0.2, 1);
  background: white;
  box-shadow: var(--card-shadow);
  position: relative;
}

.gallery-card:hover {
  transform: translateY(-10px);
  box-shadow: var(--hover-shadow);
}

.gallery-card::before {
  content: '';
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  height: 4px;
  background: linear-gradient(90deg, var(--primary-color), var(--accent-color));
  opacity: 0;
  transition: opacity 0.3s ease;
}

.gallery-card:hover::before {
  opacity: 1;
}

.card-img-container {
  height: 250px;
  overflow: hidden;
  position: relative;
}

.gallery-card img {
  width: 100%;
  height: 100%;
  object-fit: cover;
  transition: transform 0.6s ease;
}

.gallery-card:hover img {
  transform: scale(1.1);
}

.card-overlay {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: linear-gradient(to bottom, transparent 40%, rgba(0,0,0,0.8));
  opacity: 0;
  transition: opacity 0.3s ease;
  display: flex;
  align-items: flex-end;
  padding: 1.5rem;
}

.gallery-card:hover .card-overlay {
  opacity: 1;
}

.action-buttons {
  display: flex;
  gap: 0.5rem;
  transform: translateY(20px);
  transition: transform 0.3s ease;
}

.gallery-card:hover .action-buttons {
  transform: translateY(0);
}

.action-btn {
  width: 40px;
  height: 40px;
  border-radius: 50%;
  background: white;
  border: none;
  display: flex;
  align-items: center;
  justify-content: center;
  color: var(--dark-bg);
  transition: all 0.2s ease;
  box-shadow: 0 2px 10px rgba(0,0,0,0.2);
}

.action-btn:hover {
  background: var(--primary-color);
  color: white;
  transform: scale(1.1);
}

.category-tag {
  position: absolute;
  top: 15px;
  right: 15px;
  padding: 0.4rem 0.8rem;
  border-radius: 20px;
  font-size: 0.75rem;
  font-weight: 600;
  backdrop-filter: blur(10px);
  border: 1px solid rgba(255,255,255,0.2);
  display: flex;
  align-items: center;
  gap: 5px;
}

/* List View */
.gallery-list {
  background: white;
  border-radius: 20px;
  overflow: hidden;
  box-shadow: var(--card-shadow);
}

.gallery-list-item {
  padding: 1.5rem;
  border-bottom: 1px solid rgba(0,0,0,0.05);
  transition: all 0.3s ease;
  display: flex;
  align-items: center;
  gap: 1.5rem;
}

.gallery-list-item:hover {
  background: rgba(67, 97, 238, 0.03);
  transform: translateX(10px);
}

.list-thumb {
  width: 120px;
  height: 80px;
  border-radius: 12px;
  overflow: hidden;
  flex-shrink: 0;
  position: relative;
}

.list-thumb img {
  width: 100%;
  height: 100%;
  object-fit: cover;
  transition: transform 0.3s ease;
}

.gallery-list-item:hover .list-thumb img {
  transform: scale(1.1);
}

.list-content {
  flex: 1;
}

.list-content h6 {
  font-weight: 600;
  margin-bottom: 0.5rem;
  color: var(--dark-bg);
}

.list-meta {
  display: flex;
  gap: 1rem;
  align-items: center;
  margin-top: 0.5rem;
}

.meta-badge {
  padding: 0.25rem 0.75rem;
  border-radius: 15px;
  font-size: 0.75rem;
  font-weight: 600;
  display: inline-flex;
  align-items: center;
  gap: 5px;
}

.list-actions {
  display: flex;
  gap: 0.5rem;
}

/* Modern Pagination */
.pagination-modern .page-link {
  border: none;
  margin: 0 0.25rem;
  border-radius: 12px;
  color: var(--dark-bg);
  font-weight: 600;
  padding: 0.5rem 1rem;
  transition: all 0.3s ease;
  background: transparent;
}

.pagination-modern .page-item.active .page-link {
  background: linear-gradient(135deg, var(--primary-color), var(--secondary-color));
  color: white;
  box-shadow: 0 5px 15px rgba(67, 97, 238, 0.4);
}

.pagination-modern .page-link:hover {
  background: rgba(67, 97, 238, 0.1);
  transform: translateY(-2px);
}

/* Lightbox - Fullscreen Modern */
.lightbox-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.95);
  z-index: 9999;
  display: flex;
  align-items: center;
  justify-content: center;
  animation: fadeIn 0.3s ease;
}

@keyframes fadeIn {
  from { opacity: 0; }
  to { opacity: 1; }
}

.lightbox-content {
  max-width: 90vw;
  max-height: 90vh;
  position: relative;
  animation: slideUp 0.4s ease;
}

@keyframes slideUp {
  from {
    opacity: 0;
    transform: translateY(30px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

.lightbox-image {
  max-width: 100%;
  max-height: 80vh;
  object-fit: contain;
  border-radius: 10px;
  box-shadow: 0 20px 60px rgba(0,0,0,0.5);
}

.lightbox-close {
  position: absolute;
  top: -60px;
  right: 0;
  width: 45px;
  height: 45px;
  border-radius: 50%;
  background: rgba(255,255,255,0.1);
  border: none;
  color: white;
  font-size: 1.2rem;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.3s ease;
  backdrop-filter: blur(10px);
}

.lightbox-close:hover {
  background: rgba(255,255,255,0.2);
  transform: rotate(90deg);
}

.lightbox-info {
  position: absolute;
  bottom: 0;
  left: 0;
  right: 0;
  background: linear-gradient(to top, rgba(0,0,0,0.9), transparent);
  color: white;
  padding: 2rem;
  border-radius: 0 0 10px 10px;
}

.lightbox-actions {
  display: flex;
  gap: 1rem;
  margin-top: 1rem;
  flex-wrap: wrap;
}

/* Modern Modal */
.gallery-modal .modal-content {
  border-radius: 25px;
  overflow: hidden;
  border: none;
  box-shadow: 0 30px 60px rgba(0,0,0,0.3);
}

.modal-header {
  background: linear-gradient(135deg, var(--primary-color), var(--secondary-color));
  border-bottom: none;
  padding: 2rem;
}

.modal-title {
  display: flex;
  align-items: center;
  gap: 1rem;
}

.category-icon-large {
  width: 60px;
  height: 60px;
  border-radius: 50%;
  background: white;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1.5rem;
  box-shadow: 0 5px 15px rgba(0,0,0,0.2);
}

.modal-body {
  padding: 2rem;
}

.media-container {
  border-radius: 15px;
  overflow: hidden;
  margin-bottom: 2rem;
  box-shadow: 0 10px 30px rgba(0,0,0,0.1);
}

.meta-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 1rem;
  margin-top: 2rem;
}

.meta-item {
  background: var(--light-bg);
  padding: 1rem;
  border-radius: 12px;
  border-left: 4px solid var(--primary-color);
}

/* Loading Animation */
.gallery-loading {
  min-height: 70vh;
  display: flex;
  align-items: center;
  justify-content: center;
  background: linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%);
}

.loading-content {
  text-align: center;
}

.spinner-modern {
  width: 60px;
  height: 60px;
  border: 3px solid rgba(67, 97, 238, 0.1);
  border-top-color: var(--primary-color);
  border-radius: 50%;
  animation: spin 1s linear infinite;
}

@keyframes spin {
  to { transform: rotate(360deg); }
}

/* Empty State */
.empty-state {
  text-align: center;
  padding: 4rem 2rem;
  background: white;
  border-radius: 20px;
  box-shadow: var(--card-shadow);
}

.empty-icon {
  font-size: 4rem;
  color: var(--light-bg);
  margin-bottom: 1.5rem;
}

/* Responsive Design */
@media (max-width: 768px) {
  .gallery-hero {
    padding: 4rem 0 3rem;
    clip-path: polygon(0 0, 100% 0, 100% 90%, 0 100%);
  }
  
  .hero-stats {
    flex-direction: column;
    gap: 1rem;
  }
  
  .stat-box {
    width: 100%;
  }
  
  .hero-gallery-preview {
    height: 250px;
    transform: none;
    box-shadow: 0 10px 30px rgba(0,0,0,0.2);
  }
  
  .gallery-controls {
    margin-top: 0;
    border-radius: 0;
  }
  
  .gallery-list-item {
    flex-direction: column;
    align-items: stretch;
    gap: 1rem;
  }
  
  .list-thumb {
    width: 100%;
    height: 150px;
  }
  
  .list-actions {
    justify-content: flex-start;
  }
}

@media (max-width: 576px) {
  .hero-gallery-preview {
    height: 200px;
    grid-template-columns: 1fr;
    grid-template-rows: repeat(4, 1fr);
  }
  
  .preview-1, .preview-2, .preview-3 {
    grid-column: 1;
  }
  
  .preview-1 { grid-row: 1; }
  .preview-2 { grid-row: 2; }
  .preview-3 { grid-row: 3; }
  
  .gallery-card {
    margin-bottom: 1.5rem;
  }
  
  .lightbox-actions {
    justify-content: center;
  }
}

/* Animation for cards */
@keyframes cardAppear {
  from {
    opacity: 0;
    transform: translateY(20px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

.gallery-card {
  animation: cardAppear 0.5s ease forwards;
}

.gallery-card:nth-child(2) { animation-delay: 0.1s; }
.gallery-card:nth-child(3) { animation-delay: 0.2s; }
.gallery-card:nth-child(4) { animation-delay: 0.3s; }

/* Custom scrollbar */
.gallery-container ::-webkit-scrollbar {
  width: 8px;
}

.gallery-container ::-webkit-scrollbar-track {
  background: #f1f1f1;
  border-radius: 4px;
}

.gallery-container ::-webkit-scrollbar-thumb {
  background: linear-gradient(135deg, var(--primary-color), var(--secondary-color));
  border-radius: 4px;
}

.gallery-container ::-webkit-scrollbar-thumb:hover {
  background: linear-gradient(135deg, var(--secondary-color), var(--primary-color));
}

/* Hover effects */
.hover-lift {
  transition: transform 0.3s ease, box-shadow 0.3s ease;
}

.hover-lift:hover {
  transform: translateY(-5px);
  box-shadow: 0 10px 25px rgba(0,0,0,0.1);
}

/* Glass effect */
.glass-effect {
  background: rgba(255, 255, 255, 0.1);
  backdrop-filter: blur(10px);
  border: 1px solid rgba(255, 255, 255, 0.2);
}

/* Gradient text */
.gradient-text {
  background: linear-gradient(90deg, var(--primary-color), var(--accent-color));
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
}
`;

// Add styles to document
const styleSheet = document.createElement("style");
styleSheet.textContent = styles;
document.head.appendChild(styleSheet);

const GalleryDisplay = () => {
  const [galleryItems, setGalleryItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  
  // Filter states
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [selectedType, setSelectedType] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [activeTab, setActiveTab] = useState('all');
  const [sortBy, setSortBy] = useState('newest');
  const [viewMode, setViewMode] = useState('grid');
  
  // Pagination
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(12);
  const [totalItems, setTotalItems] = useState(0);
  
  // Modal states
  const [showModal, setShowModal] = useState(false);
  const [selectedItem, setSelectedItem] = useState(null);
  const [lightboxOpen, setLightboxOpen] = useState(false);
  
  // Likes state
  const [likedItems, setLikedItems] = useState([]);
  
  // Categories with icons
  const categoryIcons = {
    'service': { icon: <FaLeaf />, color: 'warning', name: 'Service' },
    'events': { icon: <FaCalendarAlt />, color: 'warning', name: 'Events' },
    'projects': { icon: <FaBox />, color: 'warning', name: 'Projects' },
    'loans': { icon: <FaPlane />, color: 'warning', name: 'Loan' },
    'investments': { icon: <FaGraduationCap />, color: 'warning', name: 'Investments' },
    'other': { icon: <FaEllipsisH />, color: 'warning', name: 'Other' }
  };

  // Initialize
  useEffect(() => {
    fetchGallery();
    const savedLikes = JSON.parse(localStorage.getItem('galleryLikes') || '[]');
    setLikedItems(savedLikes);
  }, [currentPage, selectedCategory, selectedType, sortBy]);

  const fetchGallery = async () => {
    try {
      setLoading(true);
      setError('');

      let url = `${BASE_URL}/gallery.php`;
      const params = [];
      
      if (selectedCategory !== 'all') {
        params.push(`category=${selectedCategory}`);
      }
      
      if (selectedType !== 'all') {
        params.push(`type=${selectedType}`);
      }
      
      if (searchTerm) {
        params.push(`search=${encodeURIComponent(searchTerm)}`);
      }
      
      if (params.length > 0) {
        url += `?${params.join('&')}`;
      }

      const response = await fetch(url);
      const result = await response.json();

      if (result.success && Array.isArray(result.data)) {
        let processedItems = result.data;
        processedItems = sortItems(processedItems, sortBy);
        setGalleryItems(processedItems);
        setTotalItems(processedItems.length);
      } else {
        setError('No gallery items available');
        setGalleryItems([]);
        setTotalItems(0);
      }
    } catch (err) {
      console.error(err);
      setError('Failed to load gallery');
      setGalleryItems([]);
      setTotalItems(0);
    } finally {
      setLoading(false);
    }
  };

  const sortItems = (items, sortType) => {
    const sortedItems = [...items];
    
    switch (sortType) {
      case 'newest':
        return sortedItems.sort((a, b) => new Date(b.created_at) - new Date(a.created_at));
      case 'oldest':
        return sortedItems.sort((a, b) => new Date(a.created_at) - new Date(b.created_at));
      case 'title_asc':
        return sortedItems.sort((a, b) => a.title.localeCompare(b.title));
      case 'title_desc':
        return sortedItems.sort((a, b) => b.title.localeCompare(a.title));
      default:
        return sortedItems;
    }
  };

  const getImageUrl = (imagePath) => {
    if (!imagePath) return null;
    return `${UPLOAD_BASE_URL}${imagePath}`;
  };

  const getThumbnailUrl = (imagePath) => {
    if (!imagePath) return null;
    const pathParts = imagePath.split('/');
    const fileName = pathParts[pathParts.length - 1];
    return `${UPLOAD_BASE_URL}/uploads/gallery/thumbs/${fileName}`;
  };

  const getYoutubeThumbnail = (url) => {
    if (!url) return null;
    const videoId = url.includes('youtube.com/embed/') 
      ? url.split('youtube.com/embed/')[1]
      : url.includes('youtu.be/') 
        ? url.split('youtu.be/')[1]
        : url.includes('watch?v=')
        ? url.split('watch?v=')[1].split('&')[0]
        : null;
    
    if (videoId) {
      return `https://img.youtube.com/vi/${videoId}/0.jpg`;
    }
    return null;
  };

  const getVimeoThumbnail = (url) => {
    if (!url.includes('vimeo.com')) return null;
    
    try {
      const videoId = url.split('vimeo.com/')[1].split('/')[0];
      return `https://vumbnail.com/${videoId}.jpg`;
    } catch (err) {
      console.error('Error generating Vimeo thumbnail URL:', err);
      return null;
    }
  };

  const handleCategoryFilter = (category) => {
    setSelectedCategory(category);
    setCurrentPage(1);
  };

  const handleSearch = (e) => {
    e.preventDefault();
    setCurrentPage(1);
    fetchGallery();
  };

  const handleResetFilters = () => {
    setSelectedCategory('all');
    setSelectedType('all');
    setSearchTerm('');
    setActiveTab('all');
    setSortBy('newest');
    setCurrentPage(1);
    fetchGallery();
  };

  const handleViewDetails = (item) => {
    setSelectedItem(item);
    setShowModal(true);
  };

  const handleCloseModal = () => {
    setSelectedItem(null);
    setShowModal(false);
  };

  const openLightbox = (item) => {
    if (item.gallery_type === 'photo') {
      setSelectedItem(item);
      setLightboxOpen(true);
    } else {
      handleViewDetails(item);
    }
  };

  const closeLightbox = () => {
    setLightboxOpen(false);
  };

  const handleLike = (itemId) => {
    const newLikedItems = likedItems.includes(itemId)
      ? likedItems.filter(id => id !== itemId)
      : [...likedItems, itemId];
    
    setLikedItems(newLikedItems);
    localStorage.setItem('galleryLikes', JSON.stringify(newLikedItems));
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const formatTimeAgo = (dateString) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffInSeconds = Math.floor((now - date) / 1000);
    
    if (diffInSeconds < 60) return 'Just now';
    if (diffInSeconds < 3600) return `${Math.floor(diffInSeconds / 60)} minutes ago`;
    if (diffInSeconds < 86400) return `${Math.floor(diffInSeconds / 3600)} hours ago`;
    if (diffInSeconds < 604800) return `${Math.floor(diffInSeconds / 86400)} days ago`;
    return formatDate(dateString);
  };

  const handleDownload = (item) => {
    if (item.gallery_type === 'photo' && item.image_path) {
      const link = document.createElement('a');
      link.href = getImageUrl(item.image_path);
      link.download = `${item.title.replace(/\s+/g, '_')}.jpg`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    }
  };

  const handleShare = (item) => {
    if (navigator.share) {
      navigator.share({
        title: item.title,
        text: item.description || 'Check out this gallery item',
        url: window.location.href,
      });
    } else {
      navigator.clipboard.writeText(window.location.href);
      alert('Link copied to clipboard!');
    }
  };

  const filteredItems = galleryItems.filter(item => {
    const matchesSearch = searchTerm === '' || 
                         item.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         (item.description && item.description.toLowerCase().includes(searchTerm.toLowerCase()));
    
    const matchesTab = activeTab === 'all' || 
                      (activeTab === 'photos' && item.gallery_type === 'photo') ||
                      (activeTab === 'videos' && item.gallery_type === 'video');
    
    return matchesSearch && matchesTab;
  });

  // Pagination logic
  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentItems = filteredItems.slice(indexOfFirstItem, indexOfLastItem);
  const totalPages = Math.ceil(filteredItems.length / itemsPerPage);

  const paginate = (pageNumber) => setCurrentPage(pageNumber);

  const getCategoryInfo = (category) => {
    return categoryIcons[category] || categoryIcons.other;
  };

  if (loading) {
    return (
      <div className="gallery-loading">
        <div className="loading-content">
          <div className="spinner-modern"></div>
          <h4 className="mt-4 gradient-text">Loading Gallery</h4>
          <p className="text-muted">Discovering beautiful moments...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="gallery-container">
      {/* Hero Section */}
      <section className="gallery-hero">
        <div className="hero-overlay"></div>
        <Container>
          <Row className="align-items-center">
            <Col lg={6} className="text-center text-lg-start">
              <h1 className="display-4 fw-bold">Our Gallery</h1>
              <p className="lead text-white mb-4">
                Explore our collection of photos and videos showcasing our work, 
                events, and beautiful moments captured through the lens.
              </p>
              <div className="hero-stats">
                <div className="stat-box">
                  <h3>{galleryItems.filter(i => i.gallery_type === 'photo').length}</h3>
                  <small className="text-white">Photos</small>
                </div>
                <div className="stat-box">
                  <h3>{galleryItems.filter(i => i.gallery_type === 'video').length}</h3>
                  <small className="text-white">Videos</small>
                </div>
                <div className="stat-box">
                  <h3>{galleryItems.length}</h3>
                  <small className="text-white">Total Items</small>
                </div>
              </div>
            </Col>
            <Col lg={6} className="mt-4 mt-lg-0">
              <div className="hero-gallery-preview">
                {galleryItems.slice(0, 3).map((item, index) => (
                  <div 
                    key={item.id}
                    className={`preview-item preview-${index + 1}`}
                    onClick={() => openLightbox(item)}
                  >
                    {item.gallery_type === 'photo' ? (
                      <img 
                        src={getThumbnailUrl(item.image_path) || getImageUrl(item.image_path)} 
                        alt={item.title}
                        loading="lazy"
                      />
                    ) : (
                      <div className="video-preview">
                        <img 
                          src={getYoutubeThumbnail(item.video_url) || getVimeoThumbnail(item.video_url)} 
                          alt={item.title}
                          loading="lazy"
                        />
                        <div className="play-overlay">
                          <FaPlayCircle />
                        </div>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </Col>
          </Row>
        </Container>
      </section>

      {/* Controls Section */}
      <section className="gallery-controls">
        <Container>
          <Row className="align-items-center">
            <Col md={4} className="mb-3 mb-md-0">
              <Form onSubmit={handleSearch}>
                <InputGroup>
                  <Form.Control
                    type="text"
                    placeholder="Search gallery..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                  />
                  <Button type="submit" variant="primary">
                    <FaSearch />
                  </Button>
                </InputGroup>
              </Form>
            </Col>
            
            <Col md={4} className="mb-3 mb-md-0">
              <div className="d-flex flex-wrap gap-2 justify-content-center">
                <Button
                  variant={selectedCategory === 'all' ? 'primary' : 'outline-primary'}
                  size="sm"
                  className="category-btn"
                  onClick={() => handleCategoryFilter('all')}
                >
                  All Categories
                </Button>
                {Object.entries(categoryIcons).map(([key, category]) => (
                  <Button
                    key={key}
                    variant={selectedCategory === key ? category.color : `outline-${category.color}`}
                    size="sm"
                    className="category-btn"
                    onClick={() => handleCategoryFilter(key)}
                  >
                    {React.cloneElement(category.icon, { className: 'me-1' })}
                    {category.name}
                  </Button>
                ))}
              </div>
            </Col>
            
            <Col md={4} className="d-flex justify-content-md-end gap-3">
              <div className="btn-group">
                <Button
                  variant={viewMode === 'grid' ? 'primary' : 'outline-white text-white'}
                  size="sm"
                  onClick={() => setViewMode('grid')}
                >
                  <FaThLarge />
                </Button>
                <Button
                  variant={viewMode === 'list' ? 'primary' : 'outline-white text-white'}
                  size="sm"
                  onClick={() => setViewMode('list')}
                >
                  <FaThList />
                </Button>
              </div>
              
              <DropdownButton
                variant="outline-white text-white"
                size="sm"
                title={<><FaSort className="me-1" /> Sort</>}
              >
                <Dropdown.Item 
                  active={sortBy === 'newest'}
                  onClick={() => setSortBy('newest')}
                >
                  Newest First
                </Dropdown.Item>
                <Dropdown.Item 
                  active={sortBy === 'oldest'}
                  onClick={() => setSortBy('oldest')}
                >
                  Oldest First
                </Dropdown.Item>
                <Dropdown.Item 
                  active={sortBy === 'title_asc'}
                  onClick={() => setSortBy('title_asc')}
                >
                  Title A-Z
                </Dropdown.Item>
                <Dropdown.Item 
                  active={sortBy === 'title_desc'}
                  onClick={() => setSortBy('title_desc')}
                >
                  Title Z-A
                </Dropdown.Item>
              </DropdownButton>
            </Col>
          </Row>
        </Container>
      </section>

      {/* Gallery Content */}
      <section className="gallery-content py-5">
        <Container>
          {/* Tabs */}
          <div className="mb-4">
            <Nav variant="tabs" activeKey={activeTab} onSelect={(k) => setActiveTab(k)}>
              <Nav.Item>
                <Nav.Link eventKey="all">All Media</Nav.Link>
              </Nav.Item>
              <Nav.Item>
                <Nav.Link eventKey="photos">
                  <FaImage className="me-2" /> Photos
                </Nav.Link>
              </Nav.Item>
              <Nav.Item>
                <Nav.Link eventKey="videos">
                  <FaVideo className="me-2" /> Videos
                </Nav.Link>
              </Nav.Item>
            </Nav>
          </div>

          {/* Gallery Grid/List */}
          {error || filteredItems.length === 0 ? (
            <div className="empty-state">
              <FaImage className="empty-icon" />
              <h4 className="mb-3">No Gallery Items Found</h4>
              <p className="text-muted mb-4">
                {error || 'Try different filters or search terms'}
              </p>
              <Button variant="primary" onClick={handleResetFilters}>
                Reset Filters
              </Button>
            </div>
          ) : viewMode === 'grid' ? (
            <Row className="g-4">
              {currentItems.map((item) => (
                <Col key={item.id} xl={3} lg={4} md={6} sm={12}>
                  <GalleryCard 
                    item={item}
                    onViewDetails={handleViewDetails}
                    onLike={handleLike}
                    onDownload={handleDownload}
                    onShare={handleShare}
                    onOpenLightbox={openLightbox}
                    isLiked={likedItems.includes(item.id)}
                    getImageUrl={getImageUrl}
                    getThumbnailUrl={getThumbnailUrl}
                    getYoutubeThumbnail={getYoutubeThumbnail}
                    getVimeoThumbnail={getVimeoThumbnail}
                    getCategoryInfo={getCategoryInfo}
                    formatTimeAgo={formatTimeAgo}
                  />
                </Col>
              ))}
            </Row>
          ) : (
            <div className="gallery-list">
              {currentItems.map((item) => (
                <GalleryListItem 
                  key={item.id}
                  item={item}
                  onViewDetails={handleViewDetails}
                  onLike={handleLike}
                  onDownload={handleDownload}
                  onShare={handleShare}
                  onOpenLightbox={openLightbox}
                  isLiked={likedItems.includes(item.id)}
                  getImageUrl={getImageUrl}
                  getThumbnailUrl={getThumbnailUrl}
                  getYoutubeThumbnail={getYoutubeThumbnail}
                  getVimeoThumbnail={getVimeoThumbnail}
                  getCategoryInfo={getCategoryInfo}
                  formatTimeAgo={formatTimeAgo}
                />
              ))}
            </div>
          )}

          {/* Pagination */}
          {filteredItems.length > itemsPerPage && (
            <div className="mt-5">
              <Pagination className="justify-content-center pagination-modern">
                <Pagination.Prev 
                  onClick={() => paginate(currentPage - 1)} 
                  disabled={currentPage === 1}
                >
                  <FaChevronLeft /> Previous
                </Pagination.Prev>
                
                {[...Array(totalPages)].map((_, index) => (
                  <Pagination.Item
                    key={index + 1}
                    active={index + 1 === currentPage}
                    onClick={() => paginate(index + 1)}
                  >
                    {index + 1}
                  </Pagination.Item>
                ))}
                
                <Pagination.Next 
                  onClick={() => paginate(currentPage + 1)} 
                  disabled={currentPage === totalPages}
                >
                  Next <FaChevronRight />
                </Pagination.Next>
              </Pagination>
              
              <div className="text-center mt-2 text-muted">
                Showing {indexOfFirstItem + 1}-{Math.min(indexOfLastItem, filteredItems.length)} of {filteredItems.length} items
              </div>
            </div>
          )}
        </Container>
      </section>

      {/* Lightbox Modal */}
      {lightboxOpen && selectedItem && selectedItem.gallery_type === 'photo' && (
        <div className="lightbox-overlay" onClick={closeLightbox}>
          <div className="lightbox-content" onClick={e => e.stopPropagation()}>
            <Button 
              variant="light" 
              className="lightbox-close"
              onClick={closeLightbox}
            >
              <FaTimes />
            </Button>
            
            <img 
              src={getImageUrl(selectedItem.image_path)} 
              alt={selectedItem.title}
              className="lightbox-image"
            />
            
            <div className="lightbox-info">
              <h4>{selectedItem.title}</h4>
              {selectedItem.description && (
                <p className="text-muted">{selectedItem.description}</p>
              )}
              <div className="lightbox-actions">
                <Button 
                  variant="outline-light" 
                  size="sm"
                  onClick={() => handleDownload(selectedItem)}
                >
                  <FaDownload className="me-1" /> Download
                </Button>
                <Button 
                  variant="outline-light" 
                  size="sm"
                  onClick={() => handleLike(selectedItem.id)}
                >
                  {likedItems.includes(selectedItem.id) ? (
                    <><FaHeart className="me-1" /> Liked</>
                  ) : (
                    <><FaRegHeart className="me-1" /> Like</>
                  )}
                </Button>
                <Button 
                  variant="outline-light" 
                  size="sm"
                  onClick={() => handleShare(selectedItem)}
                >
                  <FaShareAlt className="me-1" /> Share
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Gallery Item Modal */}
      {selectedItem && (
        <Modal 
          show={showModal} 
          onHide={handleCloseModal} 
          centered 
          size="lg"
          className="gallery-modal"
        >
          <Modal.Header closeButton>
            <Modal.Title className="w-100">
              <div className="modal-title">
                <div className={`category-icon-large text-${getCategoryInfo(selectedItem.category).color}`}>
                  {getCategoryInfo(selectedItem.category).icon}
                </div>
                <div>
                  <h4 className="mb-1">{selectedItem.title}</h4>
                  <div className="d-flex align-items-center gap-2">
                    <Badge bg={selectedItem.gallery_type === 'photo' ? 'info' : 'danger'}>
                      {selectedItem.gallery_type === 'photo' ? 'Photo' : 'Video'}
                    </Badge>
                    <small className="text-muted">
                      <FaCalendarAlt className="me-1" />
                      {formatDate(selectedItem.created_at)}
                    </small>
                  </div>
                </div>
              </div>
            </Modal.Title>
          </Modal.Header>
          <Modal.Body>
            {selectedItem.gallery_type === 'photo' ? (
              <div className="media-container">
                <img
                  src={getImageUrl(selectedItem.image_path)}
                  alt={selectedItem.title}
                  className="img-fluid w-100"
                  style={{ maxHeight: '400px', objectFit: 'contain' }}
                />
              </div>
            ) : (
              <div className="ratio ratio-16x9 media-container">
                <iframe
                  src={selectedItem.video_url}
                  title={selectedItem.title}
                  allowFullScreen
                ></iframe>
              </div>
            )}
            
            {selectedItem.description && (
              <div className="mb-4">
                <h5>Description</h5>
                <p className="text-muted">{selectedItem.description}</p>
              </div>
            )}
            
            <div className="meta-grid">
              <div className="meta-item">
                <strong>Category</strong>
                <div className="mt-1">
                  <Badge bg={getCategoryInfo(selectedItem.category).color}>
                    {getCategoryInfo(selectedItem.category).icon}
                    {getCategoryInfo(selectedItem.category).name}
                  </Badge>
                </div>
              </div>
              <div className="meta-item">
                <strong>Type</strong>
                <div className="mt-1">
                  <Badge bg={selectedItem.gallery_type === 'photo' ? 'info' : 'danger'}>
                    {selectedItem.gallery_type === 'photo' ? 'Photo' : 'Video'}
                  </Badge>
                </div>
              </div>
              <div className="meta-item">
                <strong>Uploaded</strong>
                <div className="mt-1">
                  <small className="text-muted">
                    <FaClock className="me-1" />
                    {formatTimeAgo(selectedItem.created_at)}
                  </small>
                </div>
              </div>
            </div>
          </Modal.Body>
          <Modal.Footer>
            <div className="d-flex justify-content-between w-100">
              <div>
                <Button
                  variant={likedItems.includes(selectedItem.id) ? "danger" : "outline-danger"}
                  size="sm"
                  onClick={() => handleLike(selectedItem.id)}
                >
                  {likedItems.includes(selectedItem.id) ? <FaHeart /> : <FaRegHeart />}
                  {likedItems.includes(selectedItem.id) ? ' Liked' : ' Like'}
                </Button>
                <Button
                  variant="outline-primary"
                  size="sm"
                  className="ms-2"
                  onClick={() => handleShare(selectedItem)}
                >
                  <FaShareAlt /> Share
                </Button>
              </div>
              <div>
                <Button variant="outline-secondary" onClick={handleCloseModal}>
                  Close
                </Button>
                <Button variant="primary" className="ms-2" onClick={() => {
                  handleCloseModal();
                  if (selectedItem.gallery_type === 'photo') {
                    openLightbox(selectedItem);
                  }
                }}>
                  {selectedItem.gallery_type === 'photo' ? 'View Full Screen' : 'Watch Video'}
                </Button>
              </div>
            </div>
          </Modal.Footer>
        </Modal>
      )}
    </div>
  );
};

// Gallery Card Component (Grid View)
const GalleryCard = ({ 
  item, 
  onViewDetails, 
  onLike, 
  onDownload, 
  onShare, 
  onOpenLightbox,
  isLiked,
  getImageUrl,
  getThumbnailUrl,
  getYoutubeThumbnail,
  getVimeoThumbnail,
  getCategoryInfo,
  formatTimeAgo
}) => {
  const [imageLoaded, setImageLoaded] = useState(false);

  return (
    <Card className="gallery-card hover-lift">
      <div className="card-img-container">
        {item.gallery_type === 'photo' ? (
          <>
            {!imageLoaded && (
              <div className="image-placeholder d-flex align-items-center justify-content-center">
                <Spinner animation="border" size="sm" variant="primary" />
              </div>
            )}
            <img
              src={getThumbnailUrl(item.image_path) || getImageUrl(item.image_path)}
              alt={item.title}
              style={{ display: imageLoaded ? 'block' : 'none' }}
              onLoad={() => setImageLoaded(true)}
              onClick={() => onOpenLightbox(item)}
              className="cursor-pointer"
            />
          </>
        ) : (
          <div 
            className="video-thumbnail w-100 h-100"
            style={{ 
              background: `url(${getYoutubeThumbnail(item.video_url) || getVimeoThumbnail(item.video_url) || 'https://via.placeholder.com/400x250?text=Video'}) center/cover no-repeat`
            }}
            onClick={() => onViewDetails(item)}
          >
            <div className="video-overlay">
              <FaPlayCircle size={48} className="text-white" />
            </div>
          </div>
        )}
        
        <div className="card-overlay">
          <div className="action-buttons">
            <Button
              variant="light"
              className="action-btn"
              onClick={() => onViewDetails(item)}
              title="View Details"
            >
              <FaEye />
            </Button>
            <Button
              variant="light"
              className="action-btn"
              onClick={() => onLike(item.id)}
              title={isLiked ? "Unlike" : "Like"}
            >
              {isLiked ? <FaHeart className="text-danger" /> : <FaRegHeart />}
            </Button>
            {item.gallery_type === 'photo' && (
              <Button
                variant="light"
                className="action-btn"
                onClick={() => onDownload(item)}
                title="Download"
              >
                <FaDownload />
              </Button>
            )}
          </div>
        </div>
        
        <Badge 
          bg={getCategoryInfo(item.category).color}
          className="category-tag"
        >
          {getCategoryInfo(item.category).icon}
        </Badge>
      </div>
      
      <Card.Body>
        <Card.Title className="h6 mb-2 text-truncate fw-semibold">{item.title}</Card.Title>
        <Card.Text className="small text-muted mb-3 line-clamp-2">
          {item.description || 'No description available'}
        </Card.Text>
        
        <div className="d-flex justify-content-between align-items-center">
          <small className="text-muted">
            <FaClock className="me-1" />
            {formatTimeAgo(item.created_at)}
          </small>
          
          <Badge bg={item.gallery_type === 'photo' ? 'info' : 'danger'} pill>
            {item.gallery_type === 'photo' ? <FaImage size={10} /> : <FaVideo size={10} />}
          </Badge>
        </div>
      </Card.Body>
    </Card>
  );
};

// Gallery List Item Component (List View)
const GalleryListItem = ({ 
  item, 
  onViewDetails, 
  onLike, 
  onDownload, 
  onShare,
  onOpenLightbox,
  isLiked,
  getImageUrl,
  getThumbnailUrl,
  getYoutubeThumbnail,
  getVimeoThumbnail,
  getCategoryInfo,
  formatTimeAgo
}) => {
  return (
    <div className="gallery-list-item hover-lift">
      <div className="list-thumb cursor-pointer" onClick={() => item.gallery_type === 'photo' ? onOpenLightbox(item) : onViewDetails(item)}>
        {item.gallery_type === 'photo' ? (
          <img
            src={getThumbnailUrl(item.image_path) || getImageUrl(item.image_path)}
            alt={item.title}
          />
        ) : (
          <div 
            className="w-100 h-100"
            style={{ 
              background: `url(${getYoutubeThumbnail(item.video_url) || getVimeoThumbnail(item.video_url)}) center/cover no-repeat`
            }}
          >
            <div className="play-overlay">
              <FaPlayCircle size={20} className="text-white" />
            </div>
          </div>
        )}
      </div>
      
      <div className="list-content">
        <h6 className="mb-1">{item.title}</h6>
        <p className="small text-muted mb-2 line-clamp-2">
          {item.description || 'No description available'}
        </p>
        <div className="list-meta">
          <Badge 
            bg={getCategoryInfo(item.category).color}
            className="meta-badge"
          >
            {getCategoryInfo(item.category).icon}
            {getCategoryInfo(item.category).name}
          </Badge>
          <Badge bg={item.gallery_type === 'photo' ? 'info' : 'danger'} pill className="meta-badge">
            {item.gallery_type === 'photo' ? 'Photo' : 'Video'}
          </Badge>
          <small className="text-muted">
            <FaClock className="me-1" />
            {formatTimeAgo(item.created_at)}
          </small>
        </div>
      </div>
      
      <div className="list-actions">
        <Button
          variant={isLiked ? "danger" : "outline-danger"}
          size="sm"
          className="action-btn"
          onClick={() => onLike(item.id)}
          title="Like"
        >
          {isLiked ? <FaHeart /> : <FaRegHeart />}
        </Button>
        <Button
          variant="outline-primary"
          size="sm"
          className="action-btn"
          onClick={() => onViewDetails(item)}
          title="View Details"
        >
          <FaEye />
        </Button>
        {item.gallery_type === 'photo' && (
          <Button
            variant="outline-secondary"
            size="sm"
            className="action-btn"
            onClick={() => onDownload(item)}
            title="Download"
          >
            <FaDownload />
          </Button>
        )}
      </div>
    </div>
  );
};

export default GalleryDisplay;